/**
 * Dexie-схема для оффлайн-хранения и outbox-очереди.
 *  - orders        — кешированные карточки заказов (полные)
 *  - ordersList    — кешированный список (для главной)
 *  - outbox        — очередь POST/PATCH/DELETE-мутаций, ожидающих отправки
 *  - photos        — Blob-ы фото до выгрузки в S3/storage
 *  - meta          — k/v метаданные (currentUserId, lastSync, etc.)
 *
 * Версионирование схемы: при изменении таблиц/индексов — bump version, добавь upgrade().
 */
import Dexie, { type Table } from 'dexie';

export interface OutboxItem {
  ownerId: string;
  leaseUntil?: number;
  id?: number;                     // auto-increment
  idempotencyKey: string;          // UUID, отправляется в заголовке
  method: 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  url: string;                     // абсолютный путь, например /v1/orders/abc/status
  body: unknown;                   // JSON-сериализуемое тело
  entity?: string;                 // 'order' | 'payment' | 'photo' | ...
  entityId?: string;
  createdAt: number;               // Date.now()
  attempts: number;
  lastError: string | null;
  status: 'pending' | 'in_flight' | 'failed';
}

export interface CachedOrder {
  id: string;
  payload: unknown;                // OrderDetail сериализованный
  serverVersion?: string;          // ETag/version с бэка (для conflict resolution)
  updatedAt: number;
}

export interface CachedListPage {
  key: string;                     // ключ запроса (сериализованные params)
  items: unknown;                  // OrderListItem[]
  total: number;
  updatedAt: number;
}

export interface PendingPhoto {
  ownerId: string;
  leaseUntil?: number;
  id: string;                      // UUID локальный (он же Idempotency-Key)
  orderId: string;
  /** Client-generated claim UUID. May upload before the claim itself is submitted. */
  paymentClaimId?: string | null;
  roomId: string | null;
  kind: 'OBJECT' | 'MEASUREMENT' | 'ADDRESS' | 'RECEIPT' | 'RESULT' | 'DEFECT' | 'QC';
  blob: Blob;
  uploadedUrl: string | null;
  createdAt: number;
  status: 'pending' | 'uploading' | 'failed' | 'done';
  lastError: string | null;
}

export interface MetaItem {
  key: string;
  value: unknown;
  updatedAt: number;
}

class FieldDB extends Dexie {
  orders!:     Table<CachedOrder, string>;
  ordersList!: Table<CachedListPage, string>;
  outbox!:     Table<OutboxItem, number>;
  photos!:     Table<PendingPhoto, string>;
  meta!:       Table<MetaItem, string>;

  constructor() {
    super('jaluzi-field-v1');
    this.version(1).stores({
      orders:     'id, updatedAt',
      ordersList: 'key, updatedAt',
      outbox:     '++id, status, createdAt, idempotencyKey',
      photos:     'id, orderId, status, createdAt',
      meta:       'key',
    });
    this.version(2).stores({
      outbox: '++id, ownerId, status, createdAt, idempotencyKey',
      photos: 'id, ownerId, orderId, status, createdAt',
    }).upgrade(async (tx) => {
      // Старые записи не имеют достоверного автора: сохраняем, но не отправляем от чужого имени.
      await tx.table('outbox').toCollection().modify({ ownerId: '', status: 'failed', lastError: 'Не определён автор старой операции' });
      await tx.table('photos').toCollection().modify({ ownerId: '', status: 'failed', lastError: 'Не определён автор старого фото' });
    });
  }
}

export const db = new FieldDB();

// ─── Удобные обёртки ────────────────────────────────────────────

export async function getMeta<T>(key: string): Promise<T | null> {
  const v = await db.meta.get(key);
  return (v?.value as T) ?? null;
}

export async function setMeta<T>(key: string, value: T): Promise<void> {
  await db.meta.put({ key, value, updatedAt: Date.now() });
}

export async function clearAllForLogout(): Promise<void> {
  await db.transaction('rw', [db.orders, db.ordersList, db.outbox, db.photos, db.meta], async () => {
    await db.orders.clear();
    await db.ordersList.clear();
    await db.meta.clear();
  });
}
